import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Send, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

export default function ChatDialog({ job, monteurId, currentUserId, monteurMode = false, open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const params = monteurId ? { monteur_id: monteurId } : {};

  const load = useCallback(async () => {
    if (!job) return;
    try {
      const { data } = await api.get(`/jobs/${job.id}/messages`, { params: monteurId ? { monteur_id: monteurId } : {} });
      setMessages(data.messages);
    } catch (e) {
      console.debug("Chatberichten ophalen mislukt (wordt opnieuw geprobeerd):", e?.message || e);
    }
  }, [job, monteurId]);

  useEffect(() => {
    if (!open || !job) return;
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [open, job, load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    await sendText(t);
    setText("");
  };

  const sendText = async (t) => {
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/jobs/${job.id}/messages`, { text: t }, { params: monteurId ? { monteur_id: monteurId } : {} });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Versturen mislukt");
    } finally {
      setSending(false);
    }
  };

  const INTEREST_TEXT = "Ik wil graag ingaan op uw aanbod. Stuur mij de nodige documenten maar door.";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-md max-w-lg p-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="font-display">Chat — {job?.titel}</DialogTitle>
          <DialogDescription className="flex items-center gap-1.5 flex-wrap">
            {job?.company_name ? <span>{job.company_name}</span> : <span>Privégesprek tussen opdrachtgever en professional.</span>}
            {job?.company_geverifieerd && (
              <span data-testid="chat-verified-badge" title="BTW-nummer geverifieerd via VIES"
                className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-sm">
                <BadgeCheck className="w-3.5 h-3.5" /> Geverifieerd
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div data-testid="chat-messages" className="h-80 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50">
          {messages.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-10">Nog geen berichten. Stuur het eerste bericht!</p>
          )}
          {messages.map((m) => {
            const mine = m.sender_id === currentUserId;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] rounded-md px-3 py-2 ${mine ? "bg-orange-500 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
                  {!mine && <p className="text-xs font-bold text-slate-500 mb-0.5">{m.sender_name}</p>}
                  <p className="text-sm whitespace-pre-wrap break-words">{m.text}</p>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="border-t border-slate-200">
          {monteurMode && (
            <div className="px-4 pt-3">
              <Button type="button" data-testid="interest-reply-btn" onClick={() => sendText(INTEREST_TEXT)} disabled={sending}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold text-sm h-auto py-2.5 whitespace-normal">
                Ik wil graag ingaan op uw aanbod. Stuur mij de nodige documenten maar door.
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2 p-4">
            <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="Typ een bericht..." className="rounded-sm border-slate-300" />
            <Button data-testid="chat-send-btn" type="submit" disabled={sending || !text.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold flex-shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
